import * as d3 from "d3";
import * as cmk_figures from "cmk_figures";

// Used for rapid protoyping, bypassing webpack
// var cmk_figures = cmk.figures; /* eslint-disable-line no-undef */
// var dc = dc; /* eslint-disable-line no-undef */
// var d3 = d3; /* eslint-disable-line no-undef */
// var crossfilter = crossfilter; /* eslint-disable-line no-undef */

class GaugeFigure extends cmk_figures.FigureBase {
    static ident() {
        return "gauge";
    }

    constructor(div_selector, fixed_size = null) {
        super(div_selector, fixed_size);
        this._tag_dimension = this._crossfilter.dimension(d => d.tag);
        this.margin = {top: 10, right: 10, bottom: 10, left: 10};
    }

    initialize(debug) {
        if (debug) this._add_scheduler_debugging();
        this.svg = this._div_selection.append("svg");
        this.plot = this.svg.append("g");
    }

    resize() {
        if (this._data.title) {
            this.margin.top = 10 + 24; // 24 from UX project
        } else {
            this.margin.top = 10;
        }

        cmk_figures.FigureBase.prototype.resize.call(this);
        this.svg.attr("width", this.figure_size.width).attr("height", this.figure_size.height);
        this._radius = Math.min(this.plot_size.width / 2, (3 / 4) * this.plot_size.height);
        this.plot.attr(
            "transform",
            "translate(" +
                (this.plot_size.width / 2 + this.margin.left) +
                ", " +
                (this._radius + this.margin.top) +
                ")"
        );
        this._render_fixed_elements();
    }

    update_gui() {
        this._crossfilter.remove(() => true);
        this._crossfilter.add(this._data.data);

        let filter_tag = null;
        if (this._data.plot_definitions && this._data.plot_definitions.length > 0)
            filter_tag =
                this._data.plot_definitions[this._data.plot_definitions.length - 1].use_tags[0];
        this._tag_dimension.filter(d => d == filter_tag);

        this.resize();
        this.render();
    }

    render() {
        this._render_levels();
        this.render_title(this._data.title, this._data.title_url);
    }

    _render_fixed_elements() {
        const limit = (7 * Math.PI) / 12;
        this.plot
            .selectAll("path.gauge_span")
            .data([null])
            .join(enter => enter.append("path").classed("gauge_span", true))
            .attr(
                "d",
                d3
                    .arc()
                    .innerRadius(this._radius * 0.75)
                    .outerRadius(this._radius * 0.85)
                    .startAngle(-limit)
                    .endAngle(limit)
            );
    }
    _render_gauge_range_labels(domain, formatter) {
        let limit = (15 * Math.PI) / 24;
        let label_rad = 0.8 * this._radius;
        let domain_labels = [
            {
                value: formatter(domain[0]),
                y: -label_rad * Math.cos(limit),
                x: label_rad * Math.sin(-limit),
            },
            {
                value: formatter(domain[1]),
                y: -label_rad * Math.cos(limit),
                x: label_rad * Math.sin(limit),
            },
        ];

        this.plot
            .selectAll("text.range")
            .data(domain_labels)
            .join("text")
            .classed("range", true)
            .text(d => d.value)
            .attr("text-anchor", "middle")
            .style("font-size", "8pt")
            .attr("x", d => d.x)
            .attr("y", d => d.y);
    }

    _render_levels() {
        const data = this._crossfilter.allFiltered();
        const plot = this._data.plot_definitions.filter(d => d.plot_type == "single_value")[0];
        if (!data.length || !plot || !plot.metric.bounds) {
            this.plot.selectAll("path.level").remove();
            this.plot.selectAll("path.single_value").remove();
            this.plot.selectAll("a.single_value").remove();
            return;
        }

        const config = new URLSearchParams(this._post_body);
        const display_range = JSON.parse(config.get("properties")).display_range;

        let domain = cmk_figures.adjust_domain(
            cmk_figures.calculate_domain(data),
            plot.metric.bounds
        );
        if (Array.isArray(display_range) && display_range[0] === "fixed")
            domain = display_range[1][1];

        domain.sort(); // Safeguards against negative number ordering or bad order. Display and clamp need good order
        const formatter = cmk_figures.plot_render_function(plot);

        this._render_gauge_range_labels(domain, formatter);

        if (domain[0] === domain[1]) return;
        const limit = (7 * Math.PI) / 12;
        const scale_x = d3.scaleLinear().domain(domain).range([-limit, limit]);
        // this.metric_thresholds_stripe(plot, domain, scale_x);
        // gauge bar
        const last_value = data[data.length - 1];
        const value = cmk_figures.renderable_value(last_value, domain, plot);
        this.plot
            .selectAll("path.single_value")
            .data([{...value, value: cmk_figures.clamp(last_value.value, domain)}])
            .join("path")
            .attr("class", "single_value")
            .attr(
                "d",
                d3
                    .arc()
                    .innerRadius(this._radius * 0.75)
                    .outerRadius(this._radius * 0.85)
                    .startAngle(d => -limit)
                    .endAngle(d => scale_x(d.value))
            );

        const svc_status_display = cmk_figures.getIn(plot, "svc_status_display");
        this.background_status_component(svc_status_display);
        cmk_figures.state_component(this, svc_status_display);
        cmk_figures.metric_value_component(
            this.plot,
            value,
            {x: 0, y: -this._radius / 10},
            {font_size: this._radius / 3, ...svc_status_display}
        );

        if (data.length > 10) this._render_histogram(domain, data);
    }

    metric_thresholds_stripe(plot, domain, scale_x) {
        this.plot
            .selectAll("path.level")
            .data(cmk_figures.make_levels(domain, plot.metric.bounds))
            .join(enter => enter.append("path"))
            .attr("class", d => "level " + d.style)
            .attr(
                "d",
                d3
                    .arc()
                    .innerRadius(this._radius * 0.71)
                    .outerRadius(this._radius * 0.73)
                    .startAngle(d => scale_x(d.from))
                    .endAngle(d => scale_x(d.to))
            )
            .selectAll("title")
            .data(d => [d])
            .join("title")
            .text(d => d.from + " -> " + d.to);
    }
    background_status_component(params) {
        const status_cls = cmk_figures.svc_status_css("background", params);

        if (status_cls) {
            let back_path = d3.path();
            back_path.arc(0, 0, this._radius * 0.69, (-13 * Math.PI) / 12, Math.PI / 12, false);
            this.plot
                .selectAll("path.status_background")
                .data([null])
                .join(enter => enter.insert("path", ":first-child"))
                .attr("class", `status_background ${status_cls}`)
                .attr("d", back_path.toString());
        } else {
            this.plot.selectAll("path.status_background").remove();
        }
    }
    _render_histogram(domain, data) {
        let num_bins = 40;
        const x = d3.scaleLinear().domain([0, num_bins]).range(domain);
        const bins = d3
            .histogram()
            .value(d => d.value)
            .thresholds(d3.range(num_bins).map(x))
            .domain(x.range())(data);

        let record_count = data.length;
        const innerRadius = this._radius * 0.87;
        const bin_scale = d3
            .scaleLinear()
            .domain([0, d3.max(bins, d => d.length)])
            .range([innerRadius, this._radius]);
        const limit = (7 * Math.PI) / 12;
        const angle_between_bins = (2 * limit) / bins.length;
        const bin_spacing = angle_between_bins * 0.05;
        this.plot
            .selectAll("path.bin")
            .data(bins)
            .join(enter => enter.append("path").classed("bin", true))
            .attr("fill", "#546679")
            .attr(
                "d",
                d3
                    .arc()
                    .innerRadius(innerRadius)
                    .outerRadius(d => bin_scale(d.length) + (d.length > 0 ? 2 : 0))
                    .startAngle((d, idx) => -limit + idx * angle_between_bins)
                    .endAngle((d, idx) => -limit + (idx + 1) * angle_between_bins - bin_spacing)
            )
            .selectAll("title")
            .data(d => [d])
            .join("title")
            .text(d => {
                let title = "";
                if (d.length == 0) return title;
                title += ((100.0 * d.length) / record_count).toPrecision(3);
                title += "%: " + d.x0.toPrecision(3);
                title += " -> " + d.x1.toPrecision(3);
                return title;
            });
    }
}

cmk_figures.figure_registry.register(GaugeFigure);
